import {
  Box,
  Flex,
  Grid,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import Layout from "../../../components/Layout";
import { BarChart } from "@mui/x-charts/BarChart";
import {
  CrmDashboardData,
  getCrmDashboard,
} from "../../../services/crmDashboardService";
import { DateTime } from "luxon";
import { FaCalendarAlt, FaChartBar, FaTrophy, FaDollarSign } from "react-icons/fa";
import { crm, CrmAvatar } from "../components/crmTheme";

const usdFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const pct = (current: number, previous: number) => {
  if (previous === 0) return current > 0 ? "+100%" : "–";
  const diff = ((current - previous) / previous) * 100;
  return (diff >= 0 ? "+" : "") + diff.toFixed(1) + "%";
};

const KpiCard = ({
  title,
  value,
  previous,
  isUsd,
  icon,
}: {
  title: string;
  value: number;
  previous: number;
  isUsd?: boolean;
  icon: React.ReactNode;
}) => {
  const display = isUsd ? usdFmt.format(value) : value.toLocaleString("pt-BR");
  const change = pct(value, previous);
  const positive = value >= previous;
  return (
    <Box
      bg={crm.surface}
      borderRadius="16px"
      p="24px"
      boxShadow={crm.shadowSm}
      border={`1px solid ${crm.border}`}
    >
      <Flex align="center" gap="12px" mb="12px">
        <Flex
          align="center"
          justify="center"
          w="40px"
          h="40px"
          borderRadius="10px"
          bg={crm.brandSoft}
          color={crm.brand}
          fontSize="18px"
          flexShrink={0}
        >
          {icon}
        </Flex>
        <Text fontSize="sm" color={crm.textMuted} fontWeight="500">
          {title}
        </Text>
      </Flex>
      <Text fontSize="32px" fontWeight="700" color={crm.text} lineHeight="1">
        {display}
      </Text>
      <Flex
        align="center"
        gap="4px"
        mt="8px"
        px="8px"
        py="3px"
        borderRadius="full"
        bg={positive ? "#f0fdf4" : "#fef2f2"}
        display="inline-flex"
      >
        <Text
          fontSize="xs"
          fontWeight="600"
          color={positive ? crm.success : crm.danger}
          style={{ textTransform: "none" }}
        >
          {positive ? "▲" : "▼"} {change} vs. período anterior
        </Text>
      </Flex>
    </Box>
  );
};

const CrmDashboard = () => {
  const auth = useAuthUser<any>();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const defaultEnd = DateTime.now().toFormat("yyyy-MM-dd");
  const defaultStart = DateTime.now().minus({ days: 30 }).toFormat("yyyy-MM-dd");

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [data, setData] = useState<CrmDashboardData | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    const result = await getCrmDashboard(
      `${startDate}T00:00:00`,
      `${endDate}T23:59:59`
    );
    setData(result);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [startDate, endDate]);

  // compute max USD for progress bars
  const maxUsd =
    data && data.salesByUser.length > 0
      ? Math.max(...data.salesByUser.map((u) => u.usd))
      : 1;

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="auto">
      <Layout>
        <Box p="24px" bg={crm.pageBg} minH="100%">
          {/* Header */}
          <Flex justify="space-between" align="center" mb="24px" flexWrap="wrap" gap="12px">
            <Flex align="center" gap="12px">
              <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
              <Box>
                <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                  Dashboard CRM
                </Text>
                <Text color={crm.textMuted} fontSize="sm" mt="2px">
                  Resumo de performance do período selecionado.
                </Text>
              </Box>
            </Flex>

            {/* Date range filter */}
            <Flex
              align="center"
              gap="8px"
              bg={crm.surface}
              border={`1px solid ${crm.border}`}
              borderRadius="10px"
              px="12px"
              py="6px"
            >
              <Box color={crm.brand} fontSize="14px">
                <FaCalendarAlt />
              </Box>
              <Input
                type="date"
                size="sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                border="none"
                outline="none"
                _focus={{ boxShadow: "none" }}
                w="140px"
                p={0}
                fontSize="sm"
              />
              <Text fontSize="sm" color={crm.textMuted}>
                até
              </Text>
              <Input
                type="date"
                size="sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                border="none"
                outline="none"
                _focus={{ boxShadow: "none" }}
                w="140px"
                p={0}
                fontSize="sm"
              />
            </Flex>
          </Flex>

          {loading && (
            <Flex justify="center" py="48px">
              <Spinner size="lg" color={crm.brand} />
            </Flex>
          )}

          {!loading && data && (
            <>
              {/* KPI Cards */}
              <Grid
                templateColumns={isMobile ? "1fr" : "repeat(2, 1fr)"}
                gap="16px"
                mb="24px"
              >
                <KpiCard
                  title="Leads fechados no período"
                  value={data.closedLeads}
                  previous={data.closedLeadsPrevious}
                  icon={<FaTrophy />}
                />
                <KpiCard
                  title="Total de vendas (USD)"
                  value={data.totalSalesUsd}
                  previous={data.totalSalesUsdPrevious}
                  isUsd
                  icon={<FaDollarSign />}
                />
              </Grid>

              {/* Sales over time chart */}
              {data.salesOverTime.length > 0 && (
                <Box
                  bg={crm.surface}
                  borderRadius="16px"
                  p="24px"
                  mb="24px"
                  boxShadow={crm.shadowSm}
                  border={`1px solid ${crm.border}`}
                >
                  <Flex align="center" gap="8px" mb="16px">
                    <Box color={crm.brand} fontSize="14px">
                      <FaChartBar />
                    </Box>
                    <Text fontWeight="700" color={crm.text} fontSize="sm">
                      Vendas no período
                    </Text>
                  </Flex>
                  <BarChart
                    height={260}
                    xAxis={[
                      {
                        data: data.salesOverTime.map((d) => d.date),
                        scaleType: "band",
                        tickLabelStyle: { fontSize: 11, fill: crm.textFaint },
                      },
                    ]}
                    yAxis={[{ tickLabelStyle: { fontSize: 11, fill: crm.textFaint } }]}
                    series={[
                      {
                        data: data.salesOverTime.map((d) => d.usd),
                        label: "USD",
                        color: crm.brand,
                        valueFormatter: (v) =>
                          v != null ? usdFmt.format(v) : "",
                      },
                    ]}
                    margin={{ left: 80, right: 20, top: 20, bottom: 40 }}
                  />
                </Box>
              )}

              {/* Bottom two-col */}
              <Grid
                templateColumns={isMobile ? "1fr" : "repeat(2, 1fr)"}
                gap="16px"
              >
                {/* Sales by user */}
                <Box
                  bg={crm.surface}
                  borderRadius="16px"
                  p="24px"
                  boxShadow={crm.shadowSm}
                  border={`1px solid ${crm.border}`}
                >
                  <Text fontWeight="700" color={crm.text} fontSize="sm" mb="16px">
                    Vendas por responsável
                  </Text>
                  {data.salesByUser.length === 0 ? (
                    <Text color={crm.textFaint} fontSize="sm">
                      Nenhuma venda no período.
                    </Text>
                  ) : (
                    data.salesByUser.slice(0, 8).map((u) => (
                      <Box key={u.userId ?? u.userName} mb="12px" _last={{ mb: 0 }}>
                        <Flex
                          justify="space-between"
                          align="center"
                          mb="4px"
                        >
                          <Flex align="center" gap="8px">
                            <CrmAvatar name={u.userName} size={28} />
                            <Box>
                              <Text fontSize="sm" fontWeight="500" color={crm.text}>
                                {u.userName}
                              </Text>
                              <Text fontSize="xs" color={crm.textMuted}>
                                {u.count} processo{u.count !== 1 ? "s" : ""}
                              </Text>
                            </Box>
                          </Flex>
                          <Text fontSize="sm" fontWeight="700" color={crm.success}>
                            {usdFmt.format(u.usd)}
                          </Text>
                        </Flex>
                        {/* Progress bar */}
                        <Box
                          h="3px"
                          bg={crm.border}
                          borderRadius="full"
                          overflow="hidden"
                        >
                          <Box
                            h="100%"
                            bg={crm.brand}
                            borderRadius="full"
                            style={{ width: `${Math.round((u.usd / maxUsd) * 100)}%` }}
                          />
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>

                {/* Today's tasks */}
                <Box
                  bg={crm.surface}
                  borderRadius="16px"
                  p="24px"
                  boxShadow={crm.shadowSm}
                  border={`1px solid ${crm.border}`}
                >
                  <Text fontWeight="700" color={crm.text} fontSize="sm" mb="16px">
                    Tasks de hoje
                  </Text>
                  {data.todayTasks.length === 0 ? (
                    <Text color={crm.textFaint} fontSize="sm">
                      Nenhuma task para hoje.
                    </Text>
                  ) : (
                    data.todayTasks.slice(0, 8).map((t) => (
                      <Flex
                        key={t.id}
                        align="center"
                        gap="10px"
                        py="10px"
                        borderBottom={`1px solid ${crm.border}`}
                        _last={{ borderBottom: "none" }}
                      >
                        <Box
                          w="8px"
                          h="8px"
                          borderRadius="full"
                          bg={crm.brand}
                          flexShrink={0}
                        />
                        <Box flex={1}>
                          <Text fontSize="sm" fontWeight="500" noOfLines={1} color={crm.text}>
                            {t.title}
                          </Text>
                          {t.prospectName && (
                            <Text fontSize="xs" color={crm.textMuted}>
                              Lead: {t.prospectName}
                            </Text>
                          )}
                          {t.statusName && (
                            <Text fontSize="xs" color={crm.textFaint}>
                              {t.statusName}
                            </Text>
                          )}
                        </Box>
                        <CrmAvatar name={t.userName} size={24} />
                      </Flex>
                    ))
                  )}
                </Box>
              </Grid>
            </>
          )}

          {!loading && !data && (
            <Flex
              justify="center"
              align="center"
              flexDir="column"
              gap="12px"
              minH="200px"
              border={`2px dashed ${crm.border}`}
              borderRadius="12px"
            >
              <Box color={crm.textFaint} fontSize="24px">
                <FaChartBar />
              </Box>
              <Text color={crm.textFaint} fontSize="sm">
                Selecione um período para carregar.
              </Text>
            </Flex>
          )}
        </Box>
      </Layout>
    </Box>
  );
};

export default CrmDashboard;
